import { useEffect, useState } from 'react';
import { IconBell, IconChevronLeft, IconHome, IconClockHour4, IconDots, IconInfoCircle, IconPlayerPause, IconPlus, IconSearch, IconSparkles, IconTrash } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { useT } from '@hana/i18n';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { ACT, APPROVALS, BRIEF, DONE, DONE_SEAT, CODE_LABEL, SCR_OPTS, TASKS, filterTasks, groupTasks, taskSums, type TaskRow, type TaskTab } from './tasks';
import { ApprovalsPane } from './ApprovalsPane';
import { DelegatesPane, TeamPane } from './PeoplePanes';
import { TasksDock } from './TasksDock';
import './i18n';
import s from './tasks.module.css';

// Mint-Tasks · Việc & Duyệt, tab "Việc của tôi" (design-specs/Mint-Tasks.md). Chỉ đọc; nút ghi báo toast.

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

const GROUP_DOT: Record<string, string> = { over: 'var(--err)', today: 'var(--d-blue)', wait: 'var(--d-gray)', next: 'var(--line)' };
const jobs = [...new Set(TASKS.map((x) => x.obj).filter((o) => /^(SE|SI|TRF)/.test(o)))];

function codeCls(c: string) {
  if (c === 'GPW' || c === 'DUN') return s.cErr;
  if (c === 'MEMO' || c === '@') return s.cMute;
  if (c === 'PRC' || c === 'SLD' || c === 'OPF' || c === 'DCF') return s.cPri;
  return '';
}

function conciseDue(k: string) {
  return k.replace(/^Quá /, '−').replace(/^Còn /, '').replace(/ (\d)/, '$1');
}

function dueCls(x: TaskRow, over: boolean) {
  if (x.wait) return s.dMute;
  if (over) return s.dErr;
  return x.dueK.startsWith('Còn 1h') || x.dueK.startsWith('Còn 2h') ? s.dWarn : '';
}

export function TasksScreen() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const forced = readState();
  const [tab, setTab] = useState<TaskTab>(() => { const v = new URLSearchParams(location.search).get('tab'); return v === 'appr' || v === 'deleg' || v === 'team' || v === 'done' ? v : 'mine'; });
  const [q, setQ] = useState('');
  const [onlyGate, setOnlyGate] = useState(false);
  const [onlyMine, setOnlyMine] = useState(false);
  const [scr, setScr] = useState('all');
  const [job, setJob] = useState('');
  const [brief, setBrief] = useState(true);
  const [moreOpen, setMoreOpen] = useState(false);
  const state: ScreenState = forced ?? 'ready';
  const [wide, setWide] = useState(() => matchMedia('(min-width: 1800px)').matches);
  useEffect(() => {
    const mq = matchMedia('(min-width: 1800px)');
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const dockOn = wide && state !== 'loading' && state !== 'error';
  const noWrite = () => toast({ msg: t('pp.noWrite') });
  const isDone = tab === 'done';
  const norm = (x: string) => x.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();
  const doneRows = DONE.filter((d) => !q || norm(d.title + d.obj + d.sub).includes(norm(q)));
  const doneOnt = DONE.filter((d) => d.ont).length;
  const rows = isDone ? [] : filterTasks(TASKS, { q, onlyGate, onlyMine, scr, job, codes: [] });
  const groups = groupTasks(rows);
  const sums = taskSums(rows);
  const open = TASKS.length;
  const tabs = [
    { key: 'mine', label: 'Việc của tôi', n: open },
    { key: 'appr', label: 'Duyệt chờ tôi', n: APPROVALS.length },
    { key: 'deleg', label: 'Tôi đang thay', n: 2 },
    { key: 'team', label: 'Team', n: 6 },
  ];
  const clearAll = () => { setQ(''); setOnlyGate(false); setOnlyMine(false); setScr('all'); setJob(''); };

  return (
    <Page kind="table" dock={dockOn ? <TasksDock showBrief={brief && tab === 'mine'} onHideBrief={() => setBrief(false)} /> : undefined}>
      <PageHeader
        title={t('ts.title')}
        lead={<HeaderTool label={t('ts.backHome')} icon={IconChevronLeft} onClick={() => void navigate({ to: '/ezy' })} />}
        crumb={<><button type="button" className={s.crumb} onClick={() => void navigate({ to: '/ezy' })}><IconHome size={16} stroke={1.8} aria-hidden="true" />{t('ts.home')}</button> <span aria-hidden="true">›</span> <span>{t('ts.title')}</span></>}
        tabs={<><Segmented label={t('ts.groups')} value={tab} onChange={(k) => { setTab(k as TaskTab); }} items={tabs} /><MoreMenu open={moreOpen} setOpen={setMoreOpen} tab={tab} setTab={setTab} /></>}
        actions={
          <>
            <HeaderTool label={t('ts.notif', { n: 5 })} icon={IconBell} n={5} onClick={() => toast({ msg: t('ts.notifToast', { n: 5 }) })} />
            <HeaderTool label={t('ts.more')} icon={IconDots} onClick={noWrite} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>{t('ts.task')}</Button>
          </>
        }
      />
      {tab === 'appr' ? <ApprovalsPane state={state} /> : tab === 'deleg' ? <DelegatesPane state={state} /> : tab === 'team' ? <TeamPane state={state} /> : (
      <div className={s.page}>
        <div className={s.bar}>
          <label className={s.search}>
            <IconSearch size={16} stroke={2.2} aria-hidden="true" />
            <input aria-label={t('pp.search')} placeholder={t('pp.searchPh')} value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <button type="button" className={s.ctl} aria-haspopup="true" onClick={noWrite}>{t('ts.kind')} ▾</button>
          <select className={s.ctl} aria-label={t('ts.byScreen')} value={scr} onChange={(e) => setScr(e.target.value)}>
            {SCR_OPTS.map(([v, k]) => <option key={v} value={v}>{k}</option>)}
          </select>
          <select className={s.ctl} aria-label={t('ts.byJob')} value={job} style={{ width: 170 }} onChange={(e) => setJob(e.target.value)}>
            <option value="">{t('ts.allJobs')}</option>
            {jobs.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <button type="button" className={s.ctl} aria-pressed={onlyGate} onClick={() => setOnlyGate((v) => !v)}>{t('ts.blocking')}</button>
          <button type="button" className={s.ctl} aria-pressed={onlyMine} onClick={() => setOnlyMine((v) => !v)}>{t('ts.onlyMine')}</button>
          <div className={s.sums}>
            <span>{t('pp.overdue')} <b className={sums.over ? s.err : undefined}>{sums.over}</b></span>
            <span>{t('ts.today')} <b>{sums.today}</b></span>
            <span>{t('ts.blocking')} <b className={sums.gate ? s.err : undefined}>{sums.gate}</b></span>
          </div>
        </div>
        <StateView
          state={state}
          title={state === 'empty' ? t('ts.emptyT') : undefined}
          desc={state === 'empty' ? t('ts.emptyD') : undefined}
          action={state === 'empty' ? <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>{t('ts.addMemo')}</Button> : state === 'error' ? <Button onClick={() => location.reload()}>{t('tasks.retry')}</Button> : undefined}
          skeleton={<Skeleton rows={9} />}
        >
          {isDone && (
            <section className={s.grp} aria-label={t('ts.done7')}>
              <div className={s.grpHd}>
                <span className={s.dot} style={{ background: 'var(--ok)' }} />
                <b>{t('ts.done7')}</b><span>{doneRows.length}</span>
                <span className={s.tip} style={{ cursor: 'default' }}>{t('pp.onTime')} {doneOnt}/{DONE.length} · {Math.round((doneOnt / DONE.length) * 100)}%</span>
              </div>
              {doneRows.map((d) => <DoneRowView key={d.id} d={d} />)}
            </section>
          )}
          {!isDone && brief && !dockOn && (
            <section className={s.brief} aria-label={t('tasks.briefTitle')}>
              <span className={s.briefIc}><IconSparkles size={18} stroke={1.9} aria-hidden="true" /></span>
              <div className={s.briefTx}>
                <div><b>{t('tasks.briefTitle')}</b><span>Ask HANA · 08:00</span></div>
                <div>{BRIEF}</div>
              </div>
              <button type="button" className={s.x} aria-label={t('tasks.briefHide')} onClick={() => setBrief(false)}>×</button>
            </section>
          )}
          {!isDone && groups.map((g) => (
            <section key={g.key} className={s.grp} aria-label={g.label}>
              <div className={s.grpHd}>
                <span className={s.dot} style={{ background: GROUP_DOT[g.key] }} />
                <b>{g.label}</b><span>{g.items.length}</span>
                {g.tip && <span className={s.tip} role="img" aria-label={g.tip} title={g.tip}><IconInfoCircle size={14} stroke={1.9} aria-hidden="true" /></span>}
              </div>
              {g.items.map((x) => <Row key={x.id} x={x} over={g.key === 'over'} onWrite={noWrite} />)}
            </section>
          ))}
          {!isDone && groups.length === 0 && (
            <div className={s.empty}>{t('ts.noMatch')} <button type="button" className={s.act} onClick={clearAll}>{t('ts.clear')}</button></div>
          )}
        </StateView>
      </div>
      )}
    </Page>
  );
}

function Row({ x, over, onWrite }: { x: TaskRow; over: boolean; onWrite: () => void }) {
  const t = useT();
  const gate = x.gate === 2 ? [t('tasks.gateBlock'), s.chipErr] : x.gate === 1 ? [t('tasks.gateWarn'), s.chipWarn] : null;
  const timed = !x.wait && /^(Còn|Quá)\s/.test(x.dueK);
  const extDis = x.noExt || x.ext >= 3;
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();
  const navigate = useNavigate();
  return (
    <div className={s.row} onClick={() => void navigate({ to: '/ezy/tasks/$kind/$id', params: { kind: 'task', id: String(x.id) } })}>
      <input type="checkbox" aria-label={t('ts.doneOf', { title: x.title })} onClick={stop} onChange={onWrite} />
      <span className={`${s.code} ${codeCls(x.code)}`} title={CODE_LABEL[x.code] ?? x.code}>{x.code}</span>
      <div className={s.main}>
        <div>{x.title}</div>
        <div><a href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>{x.obj || '—'}</a> · {x.sub}</div>
      </div>
      <div className={s.due} title={`${x.dueK} · ${x.due}`}>
        <div className={dueCls(x, over)}>{timed && <IconClockHour4 size={13} stroke={2} aria-hidden="true" />}{x.wait ? t('ts.clockStop') : conciseDue(x.dueK)}</div>
        <div>{x.due.replace(/\/\d{4} /, ' ')}</div>
      </div>
      <div className={s.seat}>{x.seat}</div>
      <div className={s.chips}>
        {gate && <span className={`${s.chip} ${gate[1]}`} title={x.gate === 2 ? t('ts.blockedBy', { a: x.gateAct }) : t('ts.reasonReq', { a: x.gateAct })}>{gate[0]}</span>}
        {x.wait && <span className={`${s.chip} ${s.chipWait}`} title={t('ts.reason', { r: x.wait })}>{t('pp.waitOut')}</span>}
        {x.ext > 0 && <span className={s.chip}>+4h {x.ext}/3</span>}
      </div>
      <div className={s.acts}>
        <button type="button" className={s.act} onClick={(e) => { stop(e); onWrite(); }}>{ACT[x.code] ?? t('ts.handle')}</button>
        {x.code === 'MEMO' && <button type="button" className={`${s.act} ${s.sq} ${s.del}`} aria-label={t('ts.delMemo')} title={t('ts.delMemo')} onClick={(e) => { stop(e); onWrite(); }}><IconTrash size={15} stroke={2} aria-hidden="true" /></button>}
        <button type="button" className={`${s.act} ${s.ext}`} aria-label={t('ts.ext4')} disabled={extDis} title={x.noExt ? t('ts.noExt', { c: x.code }) : t('ts.extN', { n: x.ext })} onClick={(e) => { stop(e); onWrite(); }}><IconPlus size={16} aria-hidden="true" />4h</button>
        <button type="button" className={`${s.act} ${s.sq}`} aria-label={t('pp.waitOut')} title={t('ts.waitTip')} onClick={(e) => { stop(e); onWrite(); }}><IconPlayerPause size={15} stroke={2} aria-hidden="true" /></button>
      </div>
    </div>
  );
}

function MoreMenu({ open, setOpen, tab, setTab }: { open: boolean; setOpen: (v: boolean) => void; tab: TaskTab; setTab: (t: TaskTab) => void }) {
  const t = useT();
  const on = tab === 'done';
  return (
    <span className={s.rel}>
      <button type="button" className={`${s.more} ${on ? s.moreOn : ''}`} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>{on ? t('pp.done') : t('ts.moreMenu')} ▾</button>
      {open && (
        <>
          <div className={s.scrim} aria-hidden="true" onClick={() => setOpen(false)} />
          <div className={s.moreMenu} role="menu" aria-label={t('ts.otherGroups')}>
            <button type="button" role="menuitem" className={s.moreItem} data-on={on} onClick={() => { setTab('done'); setOpen(false); }}>
              <span>{t('pp.done')}</span><span>{DONE.length}</span>
            </button>
          </div>
        </>
      )}
    </span>
  );
}

function DoneRowView({ d }: { d: (typeof DONE)[number] }) {
  const t = useT();
  const navigate = useNavigate();
  return (
    <div className={s.row} onClick={() => void navigate({ to: '/ezy/tasks/$kind/$id', params: { kind: 'done', id: String(d.id) } })}>
      <input type="checkbox" aria-label={t('ts.doneOf', { title: d.title })} checked disabled readOnly />
      <span className={`${s.code} ${codeCls(d.code)}`} title={CODE_LABEL[d.code] ?? d.code}>{d.code}</span>
      <div className={s.main}>
        <div>{d.title}</div>
        <div><a href="#" onClick={(e) => e.preventDefault()}>{d.obj}</a> · {d.sub} · {t('ts.doneAt', { at: d.at })}</div>
      </div>
      <div className={s.due}>
        <div className={d.ont ? s.okTx : s.dErr}>{d.ont ? t('pp.onTime') : t('ts.late210')}</div>
        <div>{d.at}</div>
      </div>
      <div className={s.seat}>{DONE_SEAT}</div>
      <div className={s.chips} />
      <div className={s.acts}><span className={d.ont ? s.okTx : s.dErr}>{d.ont ? t('pp.onTime') : t('ts.lateLbl')}</span></div>
    </div>
  );
}
